import React, { useState, useMemo } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Minus,
  Edit,
  AlertTriangle,
  Info,
  CheckCircle,
  Search,
  FolderOpen,
  Layers,
  Grid3X3,
  HelpCircle,
} from 'lucide-react';
import { cn } from '@client/lib';

interface FormDiffViewerProps {
  changes: FormDiff[];
  hierarchicalDiffs?: HierarchicalFormDiff[];
}

export const FormDiffViewer: React.FC<FormDiffViewerProps> = ({
  changes = [],
  hierarchicalDiffs = [],
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const levelIcons = {
    section: FolderOpen,
    subsection: Layers,
    questionGroup: Grid3X3,
    question: HelpCircle,
  };

  const processedHierarchicalDiffs = useMemo(() => {
    if (hierarchicalDiffs.length > 0) return hierarchicalDiffs;

    // Simple grouping by section
    const grouped = changes.reduce(
      (acc, change) => {
        if (!change.sectionId) return acc;

        const sectionKey = change.sectionId;
        if (!acc[sectionKey]) {
          acc[sectionKey] = {
            id: sectionKey,
            level: 'section' as const,
            sectionId: change.sectionId,
            sectionTitle: change.sectionTitle || 'Untitled Section',
            changes: [],
            totalChanges: 0,
            changesSummary: { additions: 0, removals: 0, modifications: 0 },
            highestSeverity: 'low' as const,
            categories: [],
            description: '',
            children: [],
          };
        }

        const section = acc[sectionKey];
        section.changes.push(change);
        section.totalChanges++;

        if (change.type === 'ADD') section.changesSummary.additions++;
        else if (change.type === 'REMOVE') section.changesSummary.removals++;
        else section.changesSummary.modifications++;

        const severityOrder = { low: 0, medium: 1, high: 2 };
        if (severityOrder[change.severity] > severityOrder[section.highestSeverity]) {
          section.highestSeverity = change.severity;
        }

        if (!section.categories.includes(change.category)) {
          section.categories.push(change.category);
        }

        return acc;
      },
      {} as Record<string, HierarchicalFormDiff>,
    );

    // Generate descriptions
    Object.values(grouped).forEach(section => {
      const { additions, removals, modifications } = section.changesSummary;
      const parts = [];
      if (additions > 0) parts.push(`${additions} added`);
      if (removals > 0) parts.push(`${removals} removed`);
      if (modifications > 0) parts.push(`${modifications} modified`);
      section.description = parts.join(', ');
    });

    return Object.values(grouped).sort((a, b) => b.totalChanges - a.totalChanges);
  }, [changes, hierarchicalDiffs]);

  const filteredDiffs = useMemo(() => {
    if (!searchTerm) return processedHierarchicalDiffs;

    return processedHierarchicalDiffs
      .filter(diff => {
        const matchesSection = diff.sectionTitle.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesChanges = diff.changes.some(
          change =>
            change.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (change.questionTitle?.toLowerCase() || '').includes(searchTerm.toLowerCase()),
        );
        return matchesSection || matchesChanges;
      })
      .map(diff => ({
        ...diff,
        changes: diff.changes.filter(
          change =>
            searchTerm === '' ||
            change.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (change.questionTitle?.toLowerCase() || '').includes(searchTerm.toLowerCase()),
        ),
      }));
  }, [processedHierarchicalDiffs, searchTerm]);

  const getChangeIcon = (type: string, size = 'w-4 h-4') => {
    switch (type) {
      case 'ADD':
        return <Plus className={`${size} text-green-600`} />;
      case 'REMOVE':
        return <Minus className={`${size} text-red-600`} />;
      case 'CHANGE':
        return <Edit className={`${size} text-blue-600`} />;
      default:
        return <Info className={`${size} text-gray-700`} />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-green-600 bg-green-50 border-green-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high':
        return <AlertTriangle className="h-4 w-4" />;
      case 'medium':
        return <Info className="h-4 w-4" />;
      case 'low':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Info className="h-4 w-4" />;
    }
  };

  const toggleNode = (nodeId: string) => {
    const newSet = new Set(expandedNodes);
    if (newSet.has(nodeId)) {
      newSet.delete(nodeId);
    } else {
      newSet.add(nodeId);
    }
    setExpandedNodes(newSet);
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  if (processedHierarchicalDiffs.length === 0) {
    return (
      <div>
        <div className="rounded-lg border border-gray-200 bg-white py-12 text-center">
          <CheckCircle className="mx-auto mb-4 h-12 w-12 text-green-500" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">No Changes</h3>
          <p className="text-gray-700">The forms are identical.</p>
        </div>
      </div>
    );
  }

  const flat = processedHierarchicalDiffs.flatMap(d => d.changes ?? []);
  const totalStats = {
    total: flat.length,
    additions: flat.filter(d => d.type === 'ADD').length,
    removals: flat.filter(d => d.type === 'REMOVE').length,
    modifications: flat.filter(d => d.type === 'CHANGE').length,
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{totalStats.total}</span>
            <span className="text-gray-700">total changes</span>
          </div>
          {totalStats.additions > 0 && (
            <div className="flex items-center gap-1 text-green-600">
              <Plus className="h-4 w-4" />
              <span>{totalStats.additions} added</span>
            </div>
          )}
          {totalStats.removals > 0 && (
            <div className="flex items-center gap-1 text-red-600">
              <Minus className="h-4 w-4" />
              <span>{totalStats.removals} removed</span>
            </div>
          )}
          {totalStats.modifications > 0 && (
            <div className="flex items-center gap-1 text-blue-600">
              <Edit className="h-4 w-4" />
              <span>{totalStats.modifications} modified</span>
            </div>
          )}
        </div>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-500" />
          <input
            type="text"
            placeholder="Search changes..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="space-y-4">
        {filteredDiffs.length === 0 ? (
          <div className="rounded-lg bg-gray-50 py-8 text-center">
            <p className="text-gray-700">No changes match your search.</p>
          </div>
        ) : (
          filteredDiffs.map(diff => {
            const isExpanded = expandedNodes.has(diff.id);
            const LevelIcon = levelIcons[diff.level];

            return (
              <div
                key={diff.id}
                className="overflow-hidden rounded-lg border border-gray-200 bg-white"
              >
                <div
                  className="flex cursor-pointer items-center justify-between p-4 transition-colors hover:bg-gray-50"
                  onClick={() => toggleNode(diff.id)}
                >
                  <div className="flex items-center space-x-3">
                    <LevelIcon className="h-5 w-5 text-gray-700" />
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{diff.sectionTitle}</h3>
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium ${getSeverityColor(diff.highestSeverity)}`}
                        >
                          {getSeverityIcon(diff.highestSeverity)}
                          <span className="ml-1 capitalize">{diff.highestSeverity}</span>
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{diff.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-700">
                      {diff.totalChanges} change{diff.totalChanges !== 1 ? 's' : ''}
                    </span>
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-500" />
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50">
                    <div className="space-y-3 p-4">
                      {diff.changes.map(change => (
                        <div
                          key={change.id}
                          className="rounded border bg-white p-3"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3">
                              {getChangeIcon(change.type)}
                              <div className="min-w-0 flex-1">
                                <p className="mb-1 text-sm text-gray-900">{change.description}</p>

                                <div className="mb-2 flex items-center gap-2 text-xs text-gray-700">
                                  <span>{change.sectionTitle}</span>
                                  {change.subsectionTitle && (
                                    <>
                                      <span>→</span>
                                      <span>{change.subsectionTitle}</span>
                                    </>
                                  )}
                                  {change.questionGroupTitle && (
                                    <>
                                      <span>→</span>
                                      <span>{change.questionGroupTitle}</span>
                                    </>
                                  )}
                                  {change.questionTitle && (
                                    <>
                                      <span>→</span>
                                      <span>{change.questionTitle}</span>
                                    </>
                                  )}
                                </div>

                                {change.type === 'CHANGE' &&
                                  (change.oldValue !== undefined ||
                                    change.newValue !== undefined) && (
                                    <div className="mt-2 rounded bg-gray-50 p-2 text-xs">
                                      {change.oldValue !== undefined && (
                                        <div className="mb-1 text-red-700">
                                          <span className="font-medium">Before:</span>{' '}
                                          {formatValue(change.oldValue)}
                                        </div>
                                      )}
                                      {change.newValue !== undefined && (
                                        <div className="text-green-700">
                                          <span className="font-medium">After:</span>{' '}
                                          {formatValue(change.newValue)}
                                        </div>
                                      )}
                                    </div>
                                  )}
                              </div>
                            </div>

                            <span
                              className={cn(
                                'rounded px-2 py-1 text-xs font-medium capitalize',
                                change.category === 'structure' && 'bg-purple-100 text-purple-700',
                                change.category === 'content' && 'bg-blue-100 text-blue-700',
                                change.category === 'validation' && 'bg-orange-100 text-orange-700',
                                change.category !== 'structure' &&
                                  change.category !== 'content' &&
                                  change.category !== 'validation' &&
                                  'bg-green-100 text-green-700',
                              )}
                            >
                              {change.category}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
